import type { ServicePageConfig } from "../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../package-pricing";

export const barhjälpPages: ServicePageConfig[] = [
  {
    // Bärhjälp — config för /barhjälp
    // Vinkel: Professionell bärhjälp — tunga, skrymmande eller ömtåliga saker som du inte kan eller vill bära själv.
    // Unik vinkel vs flytt/bortforsling: fokus på själva bärandet, inte transport eller tömning. Trappor, hisslösa hus, kassaskåp, tunga vitvaror.
    slug: "barhjalp",
    serviceCategory: "barhjälp",

    meta: {
      title: "Bärhjälp – Stark & Pålitlig Hjälp med Tunga Lyft | Ungdomskraft",
      description:
        "Boka bärhjälp snabbt och enkelt. Vi bär möbler, vitvaror, kassaskåp och annat tungt – uppför trappor, genom trånga utrymmen och utan hiss. Fast pris, ofta samma dag.",
      h1: "Bärhjälp",
    },

    hero: {
      heading: "Professionell bärhjälp",
      intro:
        "Behöver du hjälp att bära något tungt, skrymmande eller ömtåligt? Våra starka och erfarna ungdomar bär upp, ner och genom trånga utrymmen – oavsett om det gäller en soffa till fjärde våningen utan hiss, ett kassaskåp som ska flytta rum eller en ny tvättmaskin som ska in i källaren. Du slipper riskera ryggskador, repade väggar och stressiga lyft. Vi tar hand om allt det tunga, du behöver bara visa vad som ska bäras och vart.",
      heroImage: "/hero-barhjalp.png",
    },

    featureCards: {
      sinceText: "Bärhjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals privatpersoner och företag med tunga lyft. Våra ungdomar är tränade i lyftteknik och vet hur man skyddar både gods och fastighet.",
      bookYourText: "Boka din bärhjälp",
      bookDescText:
        "Ange din adress, beskriv vad som ska bäras och boka en tid som passar dig. Du ser priset direkt.",
      vehicleTitleText: "Rätt utrustning för ditt lyft",
      vehicleDescText:
        "Vi kommer utrustade med bärremmar, möbelfiltar, trappklättrare och skydd för golv och väggar. Rätt verktyg gör jobbet snabbare, säkrare och skonsammare för ditt hem.",
    },

    itemsSection: {
      heading: "Vanliga saker vi hjälper till att bära",
      items: [
        {
          name: "Soffor & fåtöljer",
          description: "Tunga hörnsoffor, bäddsoffor och skrymmande fåtöljer",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description: "Tvättmaskiner, torktumlare, kylskåp och frysar",
          image: "/washer.webp",
        },
        {
          name: "Träningsutrustning",
          description: "Crosstrainers, roddmaskiner, löpband och hemmagym",
          image: "/treadmill.webp",
        },
        {
          name: "Byråer & skåp",
          description: "Tunga garderober, byråer och bokhyllor i massivt trä",
          image: "/wardrobe.webp",
        },
        {
          name: "Sängar & madrasser",
          description: "Dubbelsängar, continental-sängar och tunga madrasser",
          image: "/bed.webp",
        },
        {
          name: "Bord & matgrupper",
          description: "Matbord i massivträ, skrivbord och konferensbord",
          image: "/table.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Professionell bärhjälp – för det du inte kan bära själv",
      mainText:
        "Det finns tillfällen när man helt enkelt behöver extra armar och styrka. Kanske har du köpt en ny soffa online och den ska upp till tredje våningen i ett hisslöst hus. Kanske ska ett tungt kassaskåp flyttas från kontoret till ett annat rum, eller en gammal tvättmaskin bytas ut i en trång källare. Det är precis sådana uppdrag Ungdomskraft löser varje dag. Vi skickar starka, unga och engagerade medarbetare som är utbildade i ergonomisk lyftteknik. De vet hur man vinklar en skrymmande soffa genom ett trångt trapphus, hur man skyddar golv och dörrkarmar med filtar och kartong, och hur man jobbar säkert på höjd eller i trånga utrymmen. Du behöver inte ha möbler nedmonterade eller förpackade – vi tar hand om allt från skydd av ytor till själva bärningen. Bärhjälp från Ungdomskraft handlar inte bara om muskelkraft. Det handlar om att göra det metodiskt och professionellt. Vi planerar varje lyft innan vi börjar: mäter dörröppningar, identifierar risker och bestämmer den bästa vägen. Resultatet är att jobbet går snabbare, ingenting skadas och du slipper den där känslan av att 'hoppas det går bra'. Allt vi bär är försäkrat under transporten, och vi ersätter eventuella skador – något som inte gäller om du ber en kompis om hjälp.",
      preparationHeading: "Förbered inför bärhjälpen",
      preparationText:
        "Se till att vägen är fri: flytta skor, mattor och småmöbler som kan vara i vägen. Mät dörrarna om du har stora möbler – vi behöver veta om vi kan bära igenom eller om demontering krävs. Om möbler ska bäras nerför trappor, se till att trappan är fri och att belysningen fungerar. Parkering nära porten underlättar om saker ska lastas på eller av ett fordon. Ju mer förberett, desto snabbare går det – och du sparar pengar.",
      additionalSections: [
        {
          heading: "Trappor, trånga hissar och svåra vinklar",
          content:
            "Det vanligaste skälet att boka bärhjälp är trappor. Svenska flerfamiljshus från 50- och 60-talet har ofta smala trapphus utan hiss, och moderna lägenheter har hissar som är för små för en soffa. Vi har bärt möbler i alla tänkbara trapphus – spiraltrappor, L-formade trapphus, brant vinds­trappa och trånga källartrappor. Med bärremmar, trappklättrare och rätt teknik löser vi det mesta. Vi skyddar alltid väggar och räcken med filtar och hörnskydd så att varken dina saker eller fastigheten skadas. Om ett föremål inte går igenom trapphuset kan vi ibland lyfta via balkong eller fönster med hjälp av rep och lyftbälten – alltid med säkerheten i fokus.",
        },
        {
          heading: "Kassaskåp, vitvaror och andra extra tunga föremål",
          content:
            "Kassaskåp, stora vitvaror och tung träningsutrustning kräver särskild hantering. Ett gammalt kassaskåp kan väga flera hundra kilo och har en svårhanterad tyngdpunkt. Stora kylskåp, kombinerade tvättmaskiner och tunga crosstrainers ställer liknande krav på styrka och teknik. Vi har erfarenhet av att flytta extra tunga föremål i trapphus och vet vilken utrustning som behövs: säckkärra, bärremmar, remmar och ibland en ramp. Vi skyddar alltid golv och väggar under hela processen. Skicka gärna bilder på föremålet och trapphuset i förväg så ger vi en exakt offert anpassad efter ditt uppdrag.",
        },
        {
          heading: "Bärhjälp vid leverans och installation",
          listItems: [
            {
              label: "Ny möbel levererad till porten",
              text: "Vi bär den till rätt rum, packar upp och monterar vid behov. Du slipper stå ensam med en 80 kg tung kartong.",
            },
            {
              label: "Vitvaror in och ut",
              text: "Vi kopplar loss den gamla tvättmaskinen, bär ut den och bär in den nya. Vi kan även koppla in vatten och el om det är enkla anslutningar.",
            },
            {
              label: "Möbler mellan rum",
              text: "Ska soffan byta plats med bokhyllan? Vi ommöblerar snabbt och smidigt utan att golv eller väggar tar stryk.",
            },
            {
              label: "Kontorsflytt inom huset",
              text: "Omorganisering av kontoret – vi bär skrivbord, skåp och hyllor mellan rum eller våningar.",
            },
          ],
          afterContent:
            "Oavsett uppdrag får du ett fast pris i förväg. Inga timkostnader som skenar, inga dolda avgifter.",
        },
        {
          heading: "Varför boka bärhjälp med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara starka armar och professionell hjälp – du bidrar också till att unga människor kommer ut i arbetslivet och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som får lära sig allt från lyftteknik och kundkontakt till logistik och ansvar. Sedan starten 2014 har vi hjälpt tusentals hushåll och företag med bärhjälp av alla slag, och under resans gång har vi gett tusentals unga deras första riktiga arbetslivserfarenhet. Det innebär att varje bärhjälp du bokar är mer än bara en tjänst – det är en investering i nästa generation. Våra medarbetare får praktisk träning i att arbeta i team, kommunicera med kunder, planera lyft och ta ansvar. De lär sig vikten av att komma i tid, vara noggrann, skydda kundens egendom och leverera ett resultat man kan vara stolt över. Det syns i kvaliteten: våra team är engagerade, energiska och måna om att varje uppdrag ska bli perfekt. Vi har byggt en kultur där ingen genväg tas – varje golv skyddas, varje hörn täcks och varje tunga möbel hanteras med respekt. Ni som kund får dessutom en partner som tar hållbarhet på allvar. Om vi bär ut gamla möbler eller vitvaror kan vi köra dem till återvinning eller välgörenhet istället för att de hamnar på trottoaren. Att välja Ungdomskraft för din bärhjälp innebär att du får ett snabbt, pålitligt och prisvärt jobb utfört – samtidigt som du stöttar ungdomar på vägen in i arbetslivet. Det är stark hjälp med mening.\n\nBärhjälp är en RUT-berättigad tjänst. Det innebär att du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan – du betalar aldrig mer än halva arbetskostnaden. Ännu ett skäl att välja professionell hjälp framför att riskera ryggen själv.",
        },
      ],
    },

    faq: [
      {
        question: "Hur många bärare kommer?",
        answer:
          "Vi skickar normalt två bärare, vilket räcker för de flesta uppdrag. Om du har extra tunga föremål som kassaskåp eller stora vitvaror, eller om det är många saker som ska bäras, kan vi skicka tre eller fyra personer. Ange vad som ska bäras vid bokning så matchar vi rätt antal.",
      },
      {
        question: "Kan ni bära upp möbler utan hiss?",
        answer:
          "Ja, det är en av våra vanligaste tjänster. Vi bär regelbundet soffor, vitvaror och andra tunga saker i trapphus utan hiss – ofta fyra till fem våningar. Vi har bärremmar, trappklättrare och skyddsmattor för att göra det säkert och effektivt.",
      },
      {
        question: "Ingår skydd av golv och väggar?",
        answer:
          "Ja, vi skyddar alltid golv, dörrkarmar och trapphus med filtar och hörnskydd som en del av tjänsten. Du behöver inte förbereda något skyddsmaterial själv.",
      },
      {
        question: "Kan ni bära ut gamla möbler och bära in nya samma dag?",
        answer:
          "Absolut. Det är vanligt att vi bär ut en gammal soffa eller tvättmaskin och bär in den nya direkt. Du bokar bara ett uppdrag och beskriver bägge delarna – vi löser det på samma besök.",
      },
      {
        question: "Vad händer om något skadas under bärningen?",
        answer:
          "Alla uppdrag är försäkrade. Om något skulle gå sönder eller skadas under bärningen ersätter vi det. Dokumentera gärna föremålets skick med en bild innan vi börjar – det gör en eventuell skadereglering smidigare.",
      },
      {
        question: "Hur snabbt kan ni komma?",
        answer:
          "Vi erbjuder ofta bärhjälp samma dag eller nästa dag. Vid hög efterfrågan kan det ta ett par dagar. Boka online så ser du tillgängliga tider direkt.",
      },
      {
        question: "Vad kostar bärhjälp?",
        answer:
          "Priset beror på vad som ska bäras, hur långt (antal trappor, avstånd) och hur många bärare som behövs. En soffa eller tvättmaskin upp tre trappor landar ofta på 995–1 495 kr (Liten eller Mellan). Du ser ett uppskattat pris direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på bärhjälp?",
        answer:
          "Absolut, bärhjälp ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller tunga lyft och bärning i eller i nära anslutning till din bostad – exempelvis att bära in en soffa, flytta en tvättmaskin eller bära möbler i trappor. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Kan ni bära saker i trapphus utan hiss?",
        answer:
          "Absolut. Det är en av de vanligaste anledningarna att boka bärhjälp. Vi bär regelbundet tunga möbler och vitvaror uppför och nerför trappor i hisslösa hus – ofta fyra till fem våningar. Med bärremmar, trappklättrare och rätt lyftteknik hanterar vi även de mest utmanande trapphusen.",
      },
      {
        question: "Erbjuder ni bärhjälp på helger och kvällar?",
        answer:
          "Ja, vi erbjuder bärhjälp även på helger och vissa kvällar. Många kunder behöver hjälp just då det passar deras schema. Boka online så ser du vilka tider som finns tillgängliga – vi försöker alltid vara flexibla.",
      },
      {
        question: "Kan jag boka bärhjälp samma dag?",
        answer:
          "Ofta ja. Vi har hög kapacitet och kan i många fall erbjuda bärhjälp redan samma dag som du bokar. Vid hög efterfrågan kan det bli nästa dag. Boka via hemsidan så ser du lediga tider direkt.",
      },
      {
        question: "Kan ni bära saker åt företag och kontor?",
        answer:
          "Ja, vi utför bärhjälp åt både privatpersoner och företag. Vi bär kontorsmöbler, serverrack, kopiatorer och annan tung kontorsutrustning mellan rum, våningar eller byggnader. Faktura med moms är inget problem.",
      },
      {
        question: "Hur många personer skickar ni vid bärhjälp?",
        answer:
          "Vi anpassar teamet efter uppdraget. För enklare lyft skickar vi normalt två bärare. Vid tunga eller skrymmande föremål som kassaskåp, träningsutrustning eller flera vitvaror kan vi skicka tre till fyra personer. Beskriv vad som ska bäras vid bokning så matchar vi rätt antal.",
      },
      {
        question: "Kan ni hjälpa till att bära saker mellan rum, inte bara in och ut?",
        answer:
          "Ja, vi erbjuder bärhjälp även inom bostaden. Det kan handla om att flytta en tung soffa till ett annat rum, bära en byrå från övervåningen till källaren eller ommöblera hela vardagsrummet. Vi skyddar golv och dörrar under hela processen.",
      },
      {
        question: "I vilka kommuner erbjuder ni bärhjälp?",
        answer:
          "Vi erbjuder bärhjälp i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.barhjalp,

    pricingRows: getPackagePricingRows("barhjalp", "sv"),

    dynamicHeadings: {
      threeSteps: "Bärhjälp i 3 enkla steg",
      step2Text:
        "Våra ungdomar skyddar golv och väggar, bär föremålet dit det ska – uppför trappor, genom trånga dörrar eller mellan rum – och ser till att allt står säkert på plats.",
      step3Title: "Uppdraget klart",
      step3Desc:
        "Dina saker står säkert på plats. Vi lämnar inte förrän du är nöjd och allt ser bra ut.",
      ctaHeading: "Boka bärhjälp idag",
      ctaText:
        "Ange din adress, beskriv vad som ska bäras och välj en tid som passar. Du ser priset direkt – vi tar hand om det tunga.",
    },

    en: {
      slug: "carrying-help",
      meta: {
        title: "Carrying Help – Strong & Reliable Heavy Lifting | Ungdomskraft",
        description:
          "Book heavy lifting help in Stockholm & Gothenburg. We carry furniture, appliances, safes and more – up stairs, through tight spaces, no lift needed. Fixed price. Book today!",
        h1: "Carrying help",
      },
      hero: {
        heading: "Professional carrying help",
        intro:
          "Need help carrying something heavy, bulky or fragile? Our strong and experienced young workers carry up, down and through tight spaces — whether it's a sofa to the fourth floor with no lift, a safe moving rooms or a new washing machine going into the basement. You avoid the risk of back injuries, scratched walls and stressful lifts. We handle all the heavy work — just show us what to carry and where.",
      },
      featureCards: {
        sinceText: "Carrying help since 2014",
        bookYourText: "Book your carrying help",
        bookDescText:
          "Enter your address, describe what needs carrying and pick a time that suits you. You see the price instantly.",
        vehicleTitleText: "Right equipment for your lift",
        vehicleDescText:
          "We arrive equipped with carrying straps, furniture blankets, stair climbers and protection for floors and walls. The right tools make the job faster, safer and gentler on your home.",
      },
      itemsSection: {
        heading: "Common items we help carry",
        items: [
          {
            name: "Sofas & armchairs",
            description: "Heavy corner sofas, sofa beds and bulky armchairs",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description: "Washing machines, tumble dryers, fridges and freezers",
            image: "/washer.webp",
          },
          {
            name: "Gym equipment",
            description: "Cross trainers, rowing machines, treadmills and home gyms",
            image: "/armchair.webp",
          },
          {
            name: "Wardrobes & dressers",
            description: "Heavy wardrobes, dressers and solid-wood bookcases",
            image: "/wardrobe.webp",
          },
          {
            name: "Beds & mattresses",
            description: "Double beds, continental beds and heavy mattresses",
            image: "/bed.webp",
          },
          {
            name: "Tables",
            description: "Solid-wood dining tables, desks and conference tables",
            image: "/table.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Professional carrying help — for what you can't carry yourself",
        mainText:
          "There are times when you simply need extra arms and strength. Perhaps you've bought a new sofa online and it needs to go up to the third floor in a building with no lift. Perhaps a heavy safe needs moving from the office to another room, or an old washing machine needs swapping out in a cramped basement. These are exactly the kind of jobs Ungdomskraft solves every day. We send strong, young and committed workers trained in ergonomic lifting technique. They know how to angle a bulky sofa through a narrow stairwell, how to protect floors and door frames with blankets and cardboard, and how to work safely at height or in confined spaces. You don't need to have furniture disassembled or packaged — we take care of everything from surface protection to the actual carrying. Carrying help from Ungdomskraft isn't just about muscle power. It's about doing it methodically and professionally. We plan every lift before we start: measure doorways, identify risks and determine the best route. The result is a faster job, nothing gets damaged and you avoid that nagging feeling of 'hope this goes well'. Everything we carry is insured during the process, and we compensate for any damage — something that doesn't apply when you ask a friend for help.",
        preparationHeading: "Prepare for your carrying help",
        preparationText:
          "Make sure the route is clear: move shoes, rugs and small furniture that could be in the way. Measure your doorways if you have large furniture — we need to know whether we can carry it through or if disassembly is required. If furniture is being carried down stairs, make sure the stairway is clear and the lighting works. Parking near the entrance helps if items are being loaded onto or off a vehicle. The more prepared you are, the faster it goes — and you save money.",
        additionalSections: [
          {
            heading: "Stairs, tight lifts and awkward angles",
            content:
              "The most common reason to book carrying help is stairs. Swedish apartment buildings from the 1950s and 60s often have narrow stairwells with no lift, and modern flats have lifts that are too small for a sofa. We've carried furniture in every kind of stairwell imaginable — spiral staircases, L-shaped stairwells, steep attic stairs and cramped basement steps. With carrying straps, stair climbers and the right technique, we handle most situations. We always protect walls and railings with blankets and corner guards so that neither your belongings nor the building get damaged. If an item won't fit through the stairwell, we can sometimes lift via a balcony or window using ropes and lifting belts — always with safety as the top priority.",
          },
          {
            heading: "Safes, appliances and other extra-heavy items",
            content:
              "Safes, large appliances and heavy gym equipment require special handling. An old safe can weigh several hundred kilograms and has an awkward centre of gravity. Large fridge-freezers, combined washer-dryers and heavy cross trainers place similar demands on strength and technique. We have experience moving extra-heavy items in stairwells and know what equipment is needed: a sack barrow, carrying straps, straps and sometimes a ramp. We always protect floors and walls throughout the process. Send us photos of the item and the stairwell in advance and we'll give you an exact quote tailored to your job.",
          },
          {
            heading: "Carrying help for deliveries and installation",
            listItems: [
              {
                label: "New furniture delivered to the entrance",
                text: "We carry it to the right room, unpack and assemble if needed. No more struggling alone with an 80 kg box.",
              },
              {
                label: "Appliances in and out",
                text: "We disconnect the old washing machine, carry it out and bring the new one in. We can also connect water and power for simple hookups.",
              },
              {
                label: "Furniture between rooms",
                text: "Want to swap the sofa and the bookcase? We rearrange quickly and smoothly without floors or walls taking a beating.",
              },
              {
                label: "In-office moves",
                text: "Office reorganisation — we carry desks, cabinets and shelving between rooms or floors.",
              },
            ],
            afterContent:
              "Whatever the job, you get a fixed price upfront. No runaway hourly rates, no hidden fees.",
          },
          {
            heading: "Why book carrying help with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get strong arms and professional help — you also contribute to young people entering the workforce and building their future. Every job is staffed by motivated young workers who learn everything from lifting technique and customer interaction to logistics and responsibility. Since our founding in 2014, we have helped thousands of households and businesses with carrying help of all kinds, and along the way we have given thousands of young people their first real work experience. That means every carrying job you book is more than just a service — it's an investment in the next generation. Our workers receive hands-on training in teamwork, customer communication, lift planning and personal accountability. They learn the importance of punctuality, protecting the customer's property and delivering a result they can be proud of. This shows in the quality of the work: our teams are engaged, energetic and determined to make every job perfect. We've built a culture where no shortcut is taken — every floor is protected, every corner is covered and every heavy piece of furniture is handled with respect. As a customer you also get a partner that takes sustainability seriously. If we carry out old furniture or appliances, we can take them to recycling or charity instead of leaving them on the pavement. Choosing Ungdomskraft for your carrying help means you get a fast, reliable and affordable job done — while supporting young people on their way into working life. It's strong help with purpose.\n\nCarrying help is eligible for the Swedish RUT tax deduction. That means you get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction is handled directly on the invoice – you never pay more than half the labour cost. Yet another reason to choose professional help instead of risking your back.",
          },
        ],
      },
      faq: [
        {
          question: "How many carriers will you send?",
          answer:
            "We normally send two carriers, which is enough for most jobs. If you have extra-heavy items like safes or large appliances, or many items to carry, we can send three or four people. Specify what needs carrying when booking and we'll match the right number.",
        },
        {
          question: "Can you carry furniture up stairs with no lift?",
          answer:
            "Yes, it's one of our most common services. We regularly carry sofas, appliances and other heavy items up stairwells without a lift — often four to five floors. We have carrying straps, stair climbers and protective mats to do it safely and efficiently.",
        },
        {
          question: "Is floor and wall protection included?",
          answer:
            "Yes, we always protect floors, door frames and stairwells with blankets and corner guards as part of the service. You don't need to prepare any protective material yourself.",
        },
        {
          question: "Can you carry out old furniture and bring in new the same day?",
          answer:
            "Absolutely. It's common for us to carry out an old sofa or washing machine and bring in the new one straight away. Just book one job and describe both parts — we handle it in the same visit.",
        },
        {
          question: "What happens if something gets damaged?",
          answer:
            "All jobs are insured. If anything is broken or damaged during the carrying, we compensate for it. It helps to document the item's condition with a photo before we start — it makes any claim process smoother.",
        },
        {
          question: "How quickly can you come?",
          answer:
            "We often offer carrying help the same day or the next day. During busy periods it may take a couple of days. Book online and you'll see available times instantly.",
        },
        {
          question: "How much does carrying help cost?",
          answer:
            "The price depends on what needs carrying, the distance (number of floors, distance) and how many carriers are needed. A sofa or washing machine up three flights often lands at SEK 995–1,495 (Small or Medium). You see an estimated price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on carrying help?",
          answer:
            "Absolutely, carrying help qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers heavy lifting and carrying in or near your home – for example carrying in a sofa, moving a washing machine or carrying furniture up stairs. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "Can you help carry things up or down stairs without a lift?",
          answer:
            "Absolutely. It's one of the most common reasons to book carrying help. We regularly carry heavy furniture and appliances up and down stairs in buildings with no lift — often four to five floors. With carrying straps, stair climbers and proper lifting technique, we handle even the most challenging stairwells.",
        },
        {
          question: "Do you offer carrying help on weekends and evenings?",
          answer:
            "Yes, we offer carrying help on weekends and some evenings. Many customers need help outside normal working hours. Book online to see available times — we always try to be flexible.",
        },
        {
          question: "Can I book carrying help for the same day?",
          answer:
            "Often yes. We have high capacity and can in many cases offer carrying help the very same day you book. During busy periods it may be the next day. Book via the website and you'll see available times instantly.",
        },
        {
          question: "Do you provide carrying help for businesses and offices?",
          answer:
            "Yes, we carry items for both private customers and businesses. We handle office furniture, server racks, copiers and other heavy office equipment between rooms, floors or buildings. Invoicing with VAT is no problem.",
        },
        {
          question: "How many people do you send for carrying help?",
          answer:
            "We match the team to the job. For simpler lifts we normally send two carriers. For heavy or bulky items like safes, gym equipment or multiple appliances, we can send three to four people. Describe what needs carrying when booking and we'll assign the right number.",
        },
        {
          question: "Can you help carry items between rooms, not just in and out?",
          answer:
            "Yes, we offer carrying help within the home as well. This could be moving a heavy sofa to another room, carrying a dresser from the upper floor to the basement, or rearranging an entire living room. We protect floors and doors throughout the process.",
        },
        {
          question: "Which areas do you offer carrying help in?",
          answer:
            "We offer carrying help throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.barhjalp,
      pricingRows: getPackagePricingRows("barhjalp", "en"),
      dynamicHeadings: {
        threeSteps: "Carrying help in 3 easy steps",
        step2Text:
          "Our workers protect floors and walls, carry the item where it needs to go — up stairs, through tight doorways or between rooms — and make sure everything is safely in place.",
        step3Title: "Job done",
        step3Desc:
          "Your items are safely in place. We don't leave until you're happy and everything looks right.",
        ctaHeading: "Book carrying help today",
        ctaText:
          "Enter your address, describe what needs carrying and pick a time. You see the price instantly — we handle the heavy stuff.",
      },
    },
  },

  {
    // Bärhjälp flytt — config för /barhjalp-flytt
    // Vinkel: Kunden har egen bil/släp eller kort flytt (tvärs över gatan) och behöver bara extra armar och ben.
    // Unik vinkel vs flytthjälp: inget fordon från oss, kunden styr transporten – vi packar, bär, lastar och lossar.
    slug: "barhjalp-flytt",
    serviceCategory: "barhjälp",

    meta: {
      title: "Bärhjälp flytt – Extra Armar & Ben till Din Flytt | Ungdomskraft",
      description:
        "Boka bärhjälp vid flytt. Du har bilen – vi har armarna. Vi packar, bär, lastar och lossar åt dig. Perfekt vid kort flytt, egen bil eller hyrd släpvagn. Fast pris.",
      h1: "Bärhjälp flytt",
    },

    hero: {
      heading: "Bärhjälp vid flytt",
      intro:
        "Har du redan en bil, släpvagn eller hyrbil? Eller ska du bara flytta tvärs över gatan? Då behöver du inte betala för en hel flyttfirma med lastbil – du behöver bara extra armar och ben. Vi skickar starka, snabba ungdomar som packar, bär ut, lastar din bil, kör med dig till nya adressen och lossar allt på plats. Du styr flytten, vi gör det tunga jobbet.",
      heroImage: "/hero-barhjalp.png",
    },

    featureCards: {
      sinceText: "Bärhjälp vid flytt sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals personer som vill flytta på egen hand men behöver extra händer. Vi vet hur man packar smart, bär effektivt och lastar tryggt.",
      bookYourText: "Boka din bärhjälp flytt",
      bookDescText:
        "Ange gamla och nya adressen, beskriv vad som ska flyttas, och boka en tid. Du ser priset direkt.",
      vehicleTitleText: "Du har bilen – vi har musklerna",
      vehicleDescText:
        "Vi behöver inget eget fordon. Vi möter upp vid din bil, släpvagn eller hyrbil och hjälper till att lasta, köra med och lossa. Har du inget fordon? Vi kan hjälpa dig boka ett.",
    },

    itemsSection: {
      heading: "Vanliga saker vi hjälper flytta",
      items: [
        {
          name: "Soffor & fåtöljer",
          description: "Hörnsoffor, bäddsoffor och tunga fåtöljer",
          image: "/sofa.webp",
        },
        {
          name: "Sängar & madrasser",
          description: "Dubbelsängar, continental-sängar och tunga madrasser",
          image: "/bed.webp",
        },
        {
          name: "Flyttkartonger",
          description: "Fulla kartonger med böcker, kläder, kök och prylar",
          image: "/box.webp",
        },
        {
          name: "Bord & stolar",
          description: "Matbord, skrivbord, stolar och matgrupper",
          image: "/table.webp",
        },
        {
          name: "Vitvaror",
          description: "Tvättmaskin, torktumlare, kylskåp och diskmaskin",
          image: "/washer.webp",
        },
        {
          name: "Garderober & byråer",
          description: "PAX-garderober, byråer, bokhyllor och hyllsystem",
          image: "/wardrobe.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bärhjälp vid flytt – du styr, vi bär",
      mainText:
        "Alla flyttar behöver inte en stor flyttfirma med lastbil. Ibland har du redan en lösning för transporten – en kompis med släp, en hyrd skåpbil eller bara en kort promenad till andra sidan gatan – men du saknar de extra armar och ben som krävs för att få ner allt från fjärde våningen och in i fordonet. Det är precis det Ungdomskraft erbjuder: ren bärhjälp vid flytt, utan att du betalar för fordon du inte behöver. Vi skickar ett team med starka, engagerade ungdomar som möter upp vid din gamla adress, packar det som inte redan är packat, bär ut allt genom trapphus och trånga dörrar, och lastar din bil, släpvagn eller hyrbil så att allt får plats och ligger säkert. Sedan kan vi följa med till den nya adressen och lossa allt – bära in, ställa möbler på rätt plats och till och med montera det som behöver skruvas ihop. Du bestämmer tempot och prioriteringen, vi levererar muskelkraften. Det här upplägget är perfekt för studenter som byter lägenhet, par som flyttar ihop med egna bilar, eller familjer som hyr en släpvagn för helgen. Du sparar tusenlappar jämfört med en traditionell flyttfirma och behåller full kontroll över din flytt.",
      preparationHeading: "Förbered inför bärhjälp flytt",
      preparationText:
        "Packa så mycket som möjligt i kartonger innan vi kommer – lösa saker tar längre tid att bära och är svårare att lasta säkert. Märk kartongerna med rum (kök, sovrum, badrum) så går det snabbare att ställa allt på rätt plats i nya bostaden. Demontera möbler som är för stora att bära hela – IKEA-garderober, sängramar och stora bord går mycket lättare att flytta i delar. Se till att din bil eller släpvagn finns parkerad nära porten och att det finns lastzon eller plats att stå. Ha nycklar till både gamla och nya bostaden redo, och se till att hissen är bokad om det finns en.",
      additionalSections: [
        {
          heading: "Kort flytt – tvärs över gatan eller inom samma hus",
          content:
            "Flyttar du bara ett par trappor upp, till grannhuset eller tvärs över gatan? Då behöver du inget fordon alls – bara extra händer. Vi bär allt mellan adresserna till fots. Det här är vanligare än man tror: studenter som byter korridor, par som flyttar ihop i samma kvarter, eller familjer som uppgraderar till en större lägenhet i samma hus. Utan fordon, utan kilometerkostnad – bara ren bärhjälp. Du betalar bara för den tid och det antal bärare du behöver, vilket ofta gör detta till det absolut billigaste sättet att flytta.",
        },
        {
          heading: "Egen bil, hyrbil eller släpvagn",
          content:
            "Många väljer att hyra en skåpbil från uthyrare som OKQ8, Circle K eller Europcar, eller att låna en släpvagn. Det är ett smart och billigt sätt att flytta – men lasten måste fortfarande bäras ut, lastas rätt och lossas på andra sidan. Vi hjälper till med alla steg: bär ut möbler och kartonger, lastar fordonet så att vikten fördelas jämnt och ingenting glider under körning, och lossar allt vid den nya adressen. Vi kan även hjälpa till med att säkra lasten med spännband och filtar. Om du hyr en skåpbil men inte vill köra den själv kan någon av oss köra – berätta bara vid bokning. Har du inget fordon? Vi erbjuder även komplett flytthjälp där vi kommer med skåpbil och sköter hela flytten från dörr till dörr.",

        },
        {
          heading: "Vad ingår i bärhjälp flytt?",
          listItems: [
            {
              label: "Packning",
              text: "Vi packar det som inte redan är packat – kök, böcker, kläder, småsaker – i kartonger eller säckar.",
            },
            {
              label: "Bärning & utbärning",
              text: "Vi bär allt från lägenheten ner till fordonet – soffor, sängar, vitvaror, kartonger, allt.",
            },
            {
              label: "Lastning",
              text: "Vi lastar din bil, släpvagn eller hyrbil smart och säkert. Rätt viktfördelning, spännband och filtar.",
            },
            {
              label: "Lossning & inbärning",
              text: "Vi lossar allt vid nya adressen, bär in och ställer möbler där du vill ha dem.",
            },
            {
              label: "Montering",
              text: "Behöver sängen skruvas ihop eller garderoben monteras? Vi fixar det på plats.",
            },
          ],
          afterContent:
            "Du väljer själv vilka moment du behöver hjälp med. Vill du bara ha hjälp med utbärning och lastning? Perfekt – boka bara det.",
        },
        {
          heading: "Varför boka bärhjälp flytt med Ungdomskraft?",
          content:
            "Det som gör Ungdomskraft unikt är att vi sysselsätter ungdomar och ger dem riktig arbetslivserfarenhet. När du bokar hos oss får du inte bara starka armar vid din flytt – du bidrar också till att unga människor kommer ut i arbetslivet och bygger sin framtid. Varje uppdrag bemannas av motiverade ungdomar som får lära sig allt från packningsteknik och lyftteknik till kundkontakt, logistik och samarbete. Sedan starten 2014 har vi hjälpt tusentals flyttar där kunden har egen bil, och under resans gång har vi gett tusentals unga deras första riktiga arbetslivserfarenhet. Det innebär att varje bärhjälp flytt du bokar är mer än bara en tjänst – det är en investering i nästa generation. Våra medarbetare får praktisk träning i att arbeta i team, kommunicera med kunder, lösa problem på plats och leverera ett resultat man kan vara stolt över. De lär sig vikten av att komma i tid, skydda kundens egendom, packa smart och arbeta effektivt. Det syns i kvaliteten: våra team är energiska, snabba och måna om att din flytt ska gå smidigt. Vi har byggt en kultur där varje flytt ses som ett tillfälle att bevisa sig – och det märks i hur noggrant och omtänksamt varje kartong hanteras, varje möbel skyddas och varje trappa bärs. Att välja Ungdomskraft för din bärhjälp flytt innebär att du får ett snabbt, pålitligt och prisvärt jobb – samtidigt som du stöttar ungdomar på vägen in i arbetslivet. Det är smart flytt med mening.\n\nBärhjälp vid flytt är en RUT-berättigad tjänst. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan, vilket gör din flytt ännu billigare. Kombinera med eget fordon så får du marknadens mest prisvärda flytthjälp.",
        },
      ],
    },

    faq: [
      {
        question: "Behöver jag ha ett eget fordon?",
        answer:
          "Inte nödvändigtvis. Om du flyttar kort – tvärs över gatan, inom samma hus eller till grannkvarteret – bär vi allt till fots. Om du flyttar längre behöver du en bil, hyrbil eller släpvagn som vi kan lasta. Vi kan hjälpa dig med tips på uthyrare om du inte har ordnat fordon ännu.",
      },
      {
        question: "Kan ni köra min hyrbil?",
        answer:
          "Ja, om du har hyrt en skåpbil men inte vill köra den själv kan någon av oss köra. Berätta vid bokning så ser vi till att det är en person med B-körkort i teamet.",
      },
      {
        question: "Hur många bärare behöver jag?",
        answer:
          "För en etta eller tvåa räcker oftast två bärare. En trea eller fyra med mycket möbler kan kräva tre till fyra personer. Ange vad som ska flyttas vid bokning så rekommenderar vi rätt antal.",
      },
      {
        question: "Hjälper ni med packning och montering?",
        answer:
          "Ja, vi kan packa det som inte redan är packat och montera möbler som behöver skruvas ihop på nya adressen. Sängramar, IKEA-garderober och skrivbord är vanliga exempel. Verktyg har vi med oss.",
      },
      {
        question: "Vad kostar bärhjälp vid flytt?",
        answer:
          "Priset beror på mängden saker, antal trappor och hur många bärare som behövs. En studentflytt med två bärare och egen bil landar ofta på 1 495–2 495 kr (Mellan eller Stor). Du ser priset direkt vid bokning.",
      },
      {
        question: "Kan jag få RUT-avdrag på bärhjälp vid flytt?",
        answer:
          "Ja, bärhjälp vid flytt ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller arbete i eller i nära anslutning till din bostad – exempelvis packning, bärning i trappor och lastning av ditt bohag. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Kan ni hjälpa samma dag?",
        answer:
          "Ofta ja – vi erbjuder bärhjälp flytt med kort varsel, ibland redan samma dag. Boka online så ser du tillgängliga tider direkt.",
      },
      {
        question: "Skyddar ni mina möbler under flytten?",
        answer:
          "Ja, vi kommer med möbelfiltar, bärremmar och skyddsmaterial för att skydda möbler, golv och dörrkarmar. Allt är försäkrat under flytten – om något skulle skadas ersätter vi det.",
      },
      {
        question: "Kan jag boka bärhjälp flytt på helger?",
        answer:
          "Ja, vi erbjuder bärhjälp vid flytt även på helger. Faktum är att lördagar och söndagar är bland våra mest populära dagar för flytthjälp. Boka online så ser du vilka helgtider som finns lediga.",
      },
      {
        question: "Vad händer om flytten tar längre tid än beräknat?",
        answer:
          "Vi ger dig ett fast pris baserat på din beskrivning av flytten. Om det visar sig att uppdraget tar längre tid kommunicerar vi det öppet och diskuterar eventuella tillägg innan vi fortsätter. Du blir aldrig överraskad av en slutnota.",
      },
      {
        question: "Har ni med er packmaterial vid flytten?",
        answer:
          "Vi kan ta med kartonger, tejp och skyddsmaterial om du behöver det – ange det vid bokning. Om du redan har packmaterial hemma behöver du inte beställa extra. Vi ser till att allt packas säkert oavsett.",
      },
      {
        question: "Kan ni bära möbler som behöver demonteras vid flytt?",
        answer:
          "Ja, vi demonterar möbler som inte går att bära hela – sängramar, garderober, stora bord och hyllsystem. Vi har verktyg med oss och monterar ihop allt igen på den nya adressen. Beskriv vilka möbler det gäller vid bokning.",
      },
      {
        question: "Är bärhjälp flytt billigare än komplett flytthjälp?",
        answer:
          "Ja, oftast betydligt billigare. Eftersom du ordnar transporten själv betalar du bara för bärhjälp – inga fordonskostnader, inga kilometertillägg. För en studentflytt kan du spara flera tusen kronor jämfört med att boka en komplett flyttfirma med lastbil.",
      },
      {
        question: "Kan ni hjälpa till att bära vid flytt till och från förråd?",
        answer:
          "Ja, vi erbjuder bärhjälp flytt till och från förråd och magasinering. Vi bär ut möbler och kartonger från din bostad, lastar din bil och lossar vid förrådet – eller tvärtom. Perfekt när du behöver mellanlagra under en renovering eller vid försäljning av bostad.",
      },
      {
        question: "I vilka kommuner erbjuder ni bärhjälp flytt?",
        answer:
          "Vi erbjuder bärhjälp flytt i hela Stockholmsområdet: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö och Österåker. Vi finns även i Göteborgsområdet: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn och Öckerö.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.barhjalp,

    pricingRows: getPackagePricingRows("barhjalp", "sv"),

    dynamicHeadings: {
      threeSteps: "Bärhjälp flytt i 3 enkla steg",
      step2Text:
        "Våra ungdomar packar, bär ut allt genom trapphus och trånga dörrar, och lastar din bil, släpvagn eller hyrbil. Sedan lossar vi allt på den nya adressen och ställer möblerna där du vill ha dem.",
      step3Title: "Allt på plats",
      step3Desc:
        "Vi lossar och bär in allt på den nya adressen. Möblerna ställs där du vill ha dem – du behöver bara visa vart.",
      ctaHeading: "Boka bärhjälp flytt idag",
      ctaText:
        "Ange gamla och nya adressen, beskriv vad som ska flyttas och välj en tid. Du ser priset direkt – du styr, vi bär.",
    },

    en: {
      slug: "carrying-help-move",
      meta: {
        title: "Carrying Help for Moving – Extra Arms & Legs | Ungdomskraft",
        description:
          "Need extra muscle for your move? We pack, carry, load and unload – you bring the car, we bring the arms. Perfect for short moves or rented trailers. Fixed price.",
        h1: "Carrying help for moving",
      },
      hero: {
        heading: "Carrying help moving",
        intro:
          "Already have a car, trailer or rental van? Or just moving across the street? Then you don't need to pay for a full-service move — you just need extra arms and legs. We send strong, fast young workers who pack, carry out, load your vehicle, travel with you to the new address and unload everything on arrival. You run the move, we do the heavy lifting. Need a van too? Check out our full moving help instead.",
      },
      featureCards: {
        sinceText: "Carrying help since 2014",
        bookYourText: "Book your carrying help",
        bookDescText:
          "Enter the old and new address, describe what needs moving, and pick a time. You see the price instantly.",
        vehicleTitleText: "You have the car — we have the muscle",
        vehicleDescText:
          "We don't bring a vehicle — we meet you at your car, trailer or rental van and help load, travel with you and unload. Want us to bring a van? Book our full moving help service instead.",
      },
      itemsSection: {
        heading: "Common items we help move",
        items: [
          {
            name: "Sofas & armchairs",
            description: "Corner sofas, sofa beds and heavy armchairs",
            image: "/sofa.webp",
          },
          {
            name: "Beds & mattresses",
            description: "Double beds, continental beds and heavy mattresses",
            image: "/bed.webp",
          },
          {
            name: "Moving boxes",
            description: "Packed boxes with books, clothes, kitchen items and belongings",
            image: "/box.webp",
          },
          {
            name: "Tables & chairs",
            description: "Dining tables, desks, chairs and dining sets",
            image: "/table.webp",
          },
          {
            name: "Appliances",
            description: "Washing machines, tumble dryers, fridges and dishwashers",
            image: "/washer.webp",
          },
          {
            name: "Wardrobes & dressers",
            description: "PAX wardrobes, dressers, bookcases and shelving units",
            image: "/wardrobe.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Carrying help for moving — you drive, we carry",
        mainText:
          "Not every move needs a full-service removal company with a lorry. Sometimes you already have a transport solution — a friend with a trailer, a rented van or just a short walk to the other side of the street — but you lack the extra arms and legs needed to get everything down from the fourth floor and into the vehicle. That's exactly what this service is: pure carrying help for your move, without paying for a vehicle you don't need. We send a team of strong, committed young workers who meet you at your old address, pack whatever isn't already packed, carry everything out through stairwells and narrow doorways, and load your car, trailer or rental van so everything fits and sits securely. Then we can travel with you to the new address and unload — carry in, place furniture where you want it and even assemble things that need putting together. You set the pace and the priorities, we deliver the muscle. This setup is perfect for students swapping flats, couples moving in together with their own cars, or families renting a trailer for the weekend. You save thousands compared to a traditional removal company and keep full control of your move. If you don't have your own vehicle and want us to handle everything — van included — check out our full moving help service.",
        preparationHeading: "Preparing for your move",
        preparationText:
          "Pack as much as possible into boxes before we arrive — loose items take longer to carry and are harder to load safely. Label the boxes by room (kitchen, bedroom, bathroom) so everything goes to the right spot in the new home faster. Disassemble furniture that is too large to carry whole — IKEA wardrobes, bed frames and large tables are much easier to move in pieces. Make sure your car or trailer is parked close to the entrance and that there is a loading zone or space to stand. Have keys to both the old and new home ready, and book the lift in advance if there is one.",
        additionalSections: [
          {
            heading: "Short move — across the street or within the same building",
            content:
              "Moving just a few flights up, to the next building or across the street? Then you don't need a vehicle at all — just extra hands. We carry everything between addresses on foot. This is more common than you'd think: students swapping corridors, couples moving in together in the same block, or families upgrading to a bigger flat in the same building. No vehicle, no mileage cost — just pure carrying help. You only pay for the time and number of carriers you need, which often makes this the cheapest way to move.",
          },
          {
            heading: "Own car, rental van or trailer",
            content:
              "Many people choose to rent a van from providers like Europcar, Hertz or a local hire company, or borrow a trailer. It's a smart and affordable way to move — but the cargo still needs to be carried out, loaded properly and unloaded at the other end. We help with every step: carry out furniture and boxes, load the vehicle so weight is evenly distributed and nothing slides during the drive, and unload everything at the new address. We can also help secure the load with ratchet straps and blankets. If you've rented a van but don't want to drive it yourself, one of us can drive — just let us know when booking. Don't have a vehicle? We also offer full moving help where we bring a van and handle the entire move from door to door.",

          },
          {
            heading: "What's included in carrying help for moving?",
            listItems: [
              {
                label: "Packing",
                text: "We pack what isn't already packed — kitchen, books, clothes, small items — into boxes or bags.",
              },
              {
                label: "Carrying out",
                text: "We carry everything from the flat down to the vehicle — sofas, beds, appliances, boxes, everything.",
              },
              {
                label: "Loading",
                text: "We load your car, trailer or rental van smartly and securely. Proper weight distribution, straps and blankets.",
              },
              {
                label: "Unloading & carrying in",
                text: "We unload everything at the new address, carry it in and place furniture where you want it.",
              },
              {
                label: "Assembly",
                text: "Need the bed frame put together or the wardrobe assembled? We handle it on-site.",
              },
            ],
            afterContent:
              "You choose which parts you need help with. Only want help with carrying out and loading? No problem — just book that.",
          },
          {
            heading: "Why book carrying help for moving with Ungdomskraft?",
            content:
              "What makes Ungdomskraft unique is that we employ young people and give them real work experience. When you book with us, you don't just get strong arms for your move — you also contribute to young people entering the workforce and building their future. Every job is staffed by motivated young workers who learn everything from packing technique and lifting technique to customer interaction, logistics and teamwork. Since our founding in 2014, we have helped thousands of moves where the customer had their own vehicle, and along the way we have given thousands of young people their first real work experience. That means every moving help job you book is more than just a service — it's an investment in the next generation. Our workers receive hands-on training in working as a team, communicating with customers, solving problems on-site and delivering a result they can be proud of. They learn the importance of punctuality, protecting the customer's belongings, packing smartly and working efficiently. This shows in the quality: our teams are energetic, fast and determined to make your move go smoothly. We've built a culture where every move is seen as an opportunity to prove oneself — and it shows in how carefully every box is handled, every piece of furniture is protected and every flight of stairs is carried. Choosing Ungdomskraft for your moving help means you get a fast, reliable and affordable job done — while supporting young people on their way into working life. It's a smart move with purpose.\n\nCarrying help for moving is eligible for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice, making your move even more affordable. Combine with your own vehicle for the best-value move on the market.",
          },
        ],
      },
      faq: [
        {
          question: "Do I need my own vehicle?",
          answer:
            "For this service, yes — for longer moves you'll need a car, rental van or trailer for us to load. For short moves (across the street or within the same building) we carry everything on foot. If you don't have a vehicle and want us to bring one, check out our full moving help service where a van is included.",
        },
        {
          question: "Can you drive my rental van?",
          answer:
            "Yes, if you've rented a van but don't want to drive it yourself, one of us can drive. Let us know when booking and we'll make sure we send someone with a driving licence.",
        },
        {
          question: "How many carriers do I need?",
          answer:
            "For a studio or one-bed flat, two carriers are usually enough. A two- or three-bed with lots of furniture may need three to four people. Describe what needs moving when booking and we'll recommend the right number.",
        },
        {
          question: "Do you help with packing and assembly?",
          answer:
            "Yes, we can pack what isn't already packed and assemble furniture that needs putting together at the new address. Bed frames, IKEA wardrobes and desks are common examples. We bring our own tools.",
        },
        {
          question: "How much does carrying help for moving cost?",
          answer:
            "The price depends on the volume of belongings, number of flights and how many carriers are needed. A student move with two carriers and your own car often comes to SEK 1,495–2,495 (Medium or Large). You see the price instantly when booking.",
        },
        {
          question: "Can I get a RUT tax deduction on carrying help for moving?",
          answer:
            "Yes, carrying help for moving qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers work in or near your home – for example packing, carrying down stairs and loading your belongings. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "Can you help the same day?",
          answer:
            "Often yes — we offer moving help at short notice, sometimes the very same day. Book online and you'll see available times straight away.",
        },
        {
          question: "Do you protect my furniture during the move?",
          answer:
            "Yes, we bring furniture blankets, carrying straps and protective material to protect furniture, floors and door frames. Everything is insured during the move — if anything gets damaged, we compensate for it.",
        },
        {
          question: "Can I book carrying help for moving on weekends?",
          answer:
            "Yes, we offer carrying help for moving on weekends as well. In fact, Saturdays and Sundays are among our most popular days for moving help. Book online to see which weekend times are available.",
        },
        {
          question: "What if my move takes longer than expected?",
          answer:
            "We give you a fixed price based on your description of the move. If the job turns out to take longer, we communicate openly and discuss any additions before continuing. You're never surprised by the final bill.",
        },
        {
          question: "Do you bring packing materials for the move?",
          answer:
            "We can bring boxes, tape and protective materials if you need them — just specify when booking. If you already have packing materials at home, there's no need to order extra. We make sure everything is packed safely either way.",
        },
        {
          question: "Can you carry furniture that needs disassembly for the move?",
          answer:
            "Yes, we disassemble furniture that can't be carried whole — bed frames, wardrobes, large tables and shelving units. We bring our own tools and reassemble everything at the new address. Describe which furniture is involved when booking.",
        },
        {
          question: "Is carrying help for moving cheaper than full moving help?",
          answer:
            "Yes, usually significantly cheaper. Since you arrange the transport yourself, you only pay for carrying help — no vehicle costs, no mileage surcharges. For a student move, you can save thousands of kronor compared to booking a full removal company with a van.",
        },
        {
          question: "Can you help with moving to or from a storage unit?",
          answer:
            "Yes, we offer carrying help for moves to and from storage units. We carry out furniture and boxes from your home, load your vehicle and unload at the storage facility — or the other way around. Perfect when you need temporary storage during a renovation or while selling your home.",
        },
        {
          question: "Which areas do you offer carrying help for moving in?",
          answer:
            "We offer carrying help for moving throughout the Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. We also operate in the Gothenburg area: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.barhjalp,
      pricingRows: getPackagePricingRows("barhjalp", "en"),
      dynamicHeadings: {
        threeSteps: "Carrying help for moving in 3 easy steps",
        step2Text:
          "Our workers pack, carry everything out through stairwells and narrow doorways, and load your car, trailer or rental van. Then we unload at the new address and place furniture where you want it.",
        step3Title: "Everything in place",
        step3Desc:
          "We unload and carry everything into the new address. Furniture is placed where you want it — just point and we'll put it there.",
        ctaHeading: "Book carrying help for your move today",
        ctaText:
          "Enter the old and new address, describe what needs moving and pick a time. You see the price instantly — you drive, we carry.",
      },
    },
  },
];
